import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	OnDestroy,
	Output,
	ViewChildren,
	QueryList,
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormControl,
	Validators,
} from '@angular/forms';
import { Subscription as RxJsSubscription } from 'rxjs';
import {
	ErrorService,
	ExplainErrorsService,
	SessionService,
} from '@app/services';
import { environment } from '@env/environment';
import { PointOfInterest } from '../point-of-interest';
import { PointOfInterestService } from '../point-of-interest.service';
import { LanguageTabComponent } from '@app/models/language';
@Component({
	selector: 'hpf-point-of-interest-form',
	templateUrl: './point-of-interest-form.component.html',
})
export class PointOfInterestFormComponent implements OnInit, OnDestroy {
	/** The model subscription */
	private modelSubscription: RxJsSubscription;
	/** The point of interest to inject in the form */
	@Input() pointOfInterest: PointOfInterest;
	/** Called the save button is clicked for a new instance */
	@Output() create = new EventEmitter<PointOfInterest>();
	/** Called the save button is clicked for an existing instance */
	@Output() update = new EventEmitter<void>();
	/** Called the delete button is clicked */
	@Output() delete = new EventEmitter<void>();
	/** Set loading state of the form */
	@Input() loading = false;
	/** Enable the deletion button */
	@Input() deletable = true;
	/** Enabled quick form */
	@Input() quickFormEnabled = environment.plugins.quickForm.enabled;
	/** Denotes if deltion in progress */
	deleting = false;
	/** Show delete modal */
	deleteModal = false;
	/** The form group to use */
	form: FormGroup;
	/** Denotes if the form is pending */
	saving = false;
	/** Check permission of update **/
	readOnly = false;
	@ViewChildren(LanguageTabComponent) languageTabs: QueryList<
		LanguageTabComponent
	>;
	/** Constructor */
	constructor(
		private formBuilder: FormBuilder,
		private errorService: ErrorService,
		public explainErrorsService: ExplainErrorsService,
		private pointOfInterestService: PointOfInterestService,
		private sessionService: SessionService
	) {}
	/** Init */
	async ngOnInit() {
		this.readOnly = !(await this.sessionService.checkPermission(
			'point_of_interest',
			'update'
		));

		// Init model
		if (!this.pointOfInterest) this.pointOfInterest = new PointOfInterest();

		// Create form
		this.form = this.formBuilder.group({
			name: new FormControl(this.pointOfInterest.props.name, [
				Validators.required,
			]),
			plateau: new FormControl(this.pointOfInterest.props.plateau, [
				Validators.required,
			]),
			categories: new FormControl(this.pointOfInterest.props.categories, [
				Validators.required,
			]),

			url: new FormControl(this.pointOfInterest.props.url, []),
			diaporama: new FormControl(
				this.pointOfInterest.props.diaporama,
				[]
			),
			top_image: new FormControl(this.pointOfInterest.props.top_image, [
				Validators.required,
			]),
			thumbnail: new FormControl(this.pointOfInterest.props.thumbnail, [
				Validators.required,
			]),
			game: new FormControl(this.pointOfInterest.props.game, []),
			published: new FormControl(this.pointOfInterest.props.published, [
				Validators.required,
			]),
		});
		// Update form when model loads or changes
		this.modelSubscription = this.pointOfInterest.subscribe(() => {
			this.updateForm();
		});
	}
	/** Destroy */
	ngOnDestroy() {
		this.modelSubscription.unsubscribe();
	}
	/** Called on form submit */
	async onSubmit(): Promise<void> {
		// Saving flag
		this.saving = true;
		try {
			// Update model
			this.updateModel();
			for (let element of this.languageTabs) {
				await element.submit();
			}
			// Creation or update ?
			if (this.pointOfInterest.isNew()) {
				// Creation
				const pointOfInterest: PointOfInterest = await this.pointOfInterestService.create(
					this.pointOfInterest.toPayload()
				);
				this.create.next(pointOfInterest);
			} else {
				// Update
				await this.pointOfInterestService.update(
					this.pointOfInterest.getId(),
					this.pointOfInterest.toPayload()
				);
				this.update.next();
			}
		} catch (error) {
			this.errorService.handle(error);
		}
		// Saving flag
		this.saving = false;
	}
	/** Update models properties from inputs values */
	private updateModel(): void {
		for (const key of Object.keys(this.form.controls)) {
			this.pointOfInterest.props[key] = this.form.get(key).value;
		}
	}
	/** Update inputs values from models properties */
	private updateForm(): void {
		this.form.setValue({
			name: this.pointOfInterest.props.name,
			plateau: this.pointOfInterest.props.plateau,
			categories: this.pointOfInterest.props.categories,

			url: this.pointOfInterest.props.url,
			diaporama: this.pointOfInterest.props.diaporama,
			top_image: this.pointOfInterest.props.top_image,
			thumbnail: this.pointOfInterest.props.thumbnail,
			game: this.pointOfInterest.props.game,
			published: this.pointOfInterest.props.published,
		});
	}
	/** Called on deletion */
	onDelete(): void {
		this.deleting = true;
		this.pointOfInterestService
			.remove(this.pointOfInterest.getId())
			.then(() => {
				this.delete.next();
			})
			.catch((error) => this.errorService.handle(error))
			.then(() => (this.deleting = false));
	}
}
